import { Box, Button, Card, CircularProgress, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  type PlanOrganizationTask,
  PlanOrganizationTaskStatus
} from '../../../../../models/PlanOrganizationTask'
import { toast } from 'react-toastify'
import { PriorityFourActivityName } from '../../../../../models/PriorityFourActivityName'
import { useAnnualPlanUserRole } from '../../../../../hooks/use-annualPlanUserRole'
import { ArrowBack } from '@mui/icons-material'
import { PriorityStatusButton } from '../PriorityStatusButton'
import { Comments } from '../../../../customComponents/comments/Comments'
import { CommentParentType } from '../../../../../models/Comment'
import { PriorityActivityCard } from '../PriorityActivityCard'
import type { Priority } from '../../../../../models/Priority'
import { OverviewStatusModals } from './OverviewStatusModals'
import { FocusBuildings } from './focusBuilding/FocusBuildings'
import { IsdDistrictProgramEnhancements } from './IsdDistrictProgramEnhancements'
import { useUpdatePlanOrgTaskStatus } from '../../../../../hooks/use-updatePlanOrgTaskStatus'
import { AnnualPlanRole } from '../../../../../models/AnnualPlanRole'
import { LockedBanner } from '../../LockedBanner'
import { useAnnualPlanningNavigation } from '../../../../../hooks/use-annualPlanningNavigation'
import { PRIORITY_CONTENT } from '../../../../../constants/priorityContent'

interface Props {
  priority: Priority
  planOrganizationTask: PlanOrganizationTask
  onBack: () => void
  onPlanOrgTaskUpdate: (updatedTask: PlanOrganizationTask) => void
  implementationPhase: boolean
}

export const PriorityFourOverview = (props: Props): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { planId } = useParams()
  const { userRole, isLoading, error } = useAnnualPlanUserRole(planId != null ? Number(planId) : 0)

  const [selectedPriorityActivity, setSelectedPriorityActivity] = useState<string | null>(null)

  const {
    isUpdating,
    handleConfirmLockModal,
    updatePlanOrgTaskStatus,
    statusForLockModal,
    setStatusForLockModal,
    statusForSomeItemsLockModal,
    setStatusForSomeItemsLockModal,
    statusForSubmittedToInProgressModal,
    setStatusForSubmittedToInProgressModal,
    statusForSubmittedToAcceptedModal,
    setStatusForSubmittedToAcceptedModal,
    statusForSubmittedToNeedsRevisionModal,
    setStatusForSubmittedToNeedsRevisionModal,
    handleConfirmSubmittedToNeedsRevision,
    disableStatus,
    readOnly,
    trackingReadonly
  } = useUpdatePlanOrgTaskStatus({
    userRole,
    annualPlanId: Number(planId),
    planOrganizationTask: props.planOrganizationTask,
    onPlanOrgTaskUpdate: props.onPlanOrgTaskUpdate,
    priorityActivities: props.priority.activities,
    onBack: props.onBack,
    contractPhase: !props.implementationPhase
  })
  const { handleNavigateToContract } = useAnnualPlanningNavigation()

  const openCommentsQuery = searchParams.get('comments')
  const activityQuery = searchParams.get('activity')

  const priorityStatusButtonRef = useRef<HTMLButtonElement>(null)
  const [priorityStatusButtonHighlighted, setPriorityStatusButtonHighlighted] = useState(false)

  useEffect(() => {
    if (activityQuery != null) {
      handlePriorityActivityClick(activityQuery)
    }
  }, [activityQuery])

  const handleStatusUpdate = (newStatus: PlanOrganizationTaskStatus): void => {
    updatePlanOrgTaskStatus(newStatus)
    setPriorityStatusButtonHighlighted(false)
  }

  const handlePriorityActivityClick = (priorityActivity: string): void => {
    if (props.planOrganizationTask.status === PlanOrganizationTaskStatus.NotStarted && (userRole === AnnualPlanRole.PlanContributor || userRole === AnnualPlanRole.PlanAdministrator)) {
      toast.error('Set the status to “In Progress” in order to continue.')
      setPriorityStatusButtonHighlighted(true)
      if (priorityStatusButtonRef.current != null) {
        priorityStatusButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    setSelectedPriorityActivity(priorityActivity)
  }

  const handleBackToPriorityActivities = (): void => {
    props.onBack()
  }

  const handleBackToOverview = (): void => {
    // remove activity from search params
    if (searchParams.has('activity')) {
      searchParams.delete('activity')
      setSearchParams(searchParams)
    }
    setSelectedPriorityActivity(null)
    props.onPlanOrgTaskUpdate(props.planOrganizationTask) // trigger reload of the task
  }

  const DisplaySelectedActivity = (_props: { priorityActivity: string }): JSX.Element => {
    const isTracking = [
      PlanOrganizationTaskStatus.TrackingInProgress,
      PlanOrganizationTaskStatus.TrackingCompleted
    ].includes(props.planOrganizationTask.status)

    switch (_props.priorityActivity) {
      case PriorityFourActivityName.FocusBuildings:
        return <FocusBuildings
          priorityId={props.priority.id}
          planOrgId={props.planOrganizationTask.planOrganizationId}
          isdOrgId={props.planOrganizationTask.organizationId}
          userRole={userRole}
          readOnly={readOnly}
          trackingReadonly={trackingReadonly}
          onBack={handleBackToOverview}
          implementationPhase={props.implementationPhase}
        />
      case PriorityFourActivityName.ProgramEnrichment:
        return <IsdDistrictProgramEnhancements
          priorityId={props.priority.id}
          planOrgId={props.planOrganizationTask.planOrganizationId}
          isdOrgId={props.planOrganizationTask.organizationId}
          readOnly={readOnly}
          trackingReadonly={trackingReadonly}
          onBack={handleBackToOverview}
          handleViewContract={handleNavigateToContract}
          implementationPhase={props.implementationPhase}
          isTracking={isTracking}
        />
      default:
        return <Typography variant='body1'>Unknown Priority Activity</Typography>
    }
  }

  if (isLoading) {
    return <CircularProgress data-testid='priorityFourSpinner'/>
  }

  if (error != null) {
    return <Typography variant='body1'>{error}</Typography>
  }

  return <>
    {readOnly && (
      <LockedBanner status={props.planOrganizationTask.status} />
    )}

    <Card sx={{
      backgroundColor: 'var(--light-blue-50)',
      borderRadius: '12px',
      padding: '20px 40px 40px 40px',
      display: 'flex',
      flexDirection: 'column',
      gap: '40px',
      position: 'relative',
      overflow: 'visible'
    }}
    >
      {selectedPriorityActivity != null
        ? <DisplaySelectedActivity priorityActivity={selectedPriorityActivity} />
        : <>
          <Box data-testid='priorityFourOverview' sx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                data-testid='priorityActivitiesButton'
                onClick={handleBackToPriorityActivities}
                sx={{
                  padding: '5px 10px',
                  backgroundColor: 'var(--grey-50)',
                  color: 'var(--grey-500)',
                  textTransform: 'none'
                }}
              >
                <ArrowBack sx={{ mr: '10px' }} />
                Priority Activities
              </Button>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '13px'
              }}
              >
                <PriorityStatusButton
                  ref={priorityStatusButtonRef}
                  currentStatus={props.planOrganizationTask.status}
                  onChange={handleStatusUpdate}
                  userRole={userRole}
                  disabled={isUpdating || disableStatus}
                  highlighted={priorityStatusButtonHighlighted}
                  implementationPhase={props.implementationPhase}
                />

                <Comments
                  parentType={CommentParentType.PlanOrganizationTask}
                  parentId={props.priority.planOrganizationTaskId}
                  showComments={openCommentsQuery === 'true'}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <Box sx={{
                backgroundColor: 'var(--light-blue-700)',
                minWidth: '91px',
                minHeight: '91px',
                display: 'grid',
                placeContent: 'center',
                borderRadius: '12px'
              }}
              >
                <Typography variant='h1' sx={{ fontSize: '40px', color: 'var(--white)', fontVariantNumeric: 'lining-nums' }}>
                  04
                </Typography>
              </Box>

              <Typography variant='h1'>
                Peer to Peer Programs
              </Typography>
            </Box>
          </Box>

          <Box sx={{ backgroundColor: 'var(--white)', border: '1px solid var(--blue-300)', borderRadius: '8px', padding: '20px' }}>
            <Typography variant='h5' color='var(--blue-700)'>Overall Goals</Typography>
            <Typography variant='body1' sx={{ m: '10px 0' }}>{PRIORITY_CONTENT[4].overallGoals}</Typography>
            <ul style={{ margin: '0' }}>
              {PRIORITY_CONTENT[4].listItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </Box>

          <Typography variant='h2'>Core Activities</Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '26px' }}>
            {props.priority.activities.map(activity =>
              <PriorityActivityCard
                key={activity.name}
                name={activity.name}
                started={activity.started}
                backgroundColor='var(--light-blue-700)'
                onClick={() => { handlePriorityActivityClick(activity.name) }}
              />
            )}
          </Box>
        </>
      }

      <OverviewStatusModals
        statusForLockModal={statusForLockModal}
        handleConfirmLockModal={handleConfirmLockModal}
        handleCloseLockModal={() => { setStatusForLockModal(null) }}
        statusForSomeItemsLockModal={statusForSomeItemsLockModal}
        handleConfirmNoItemsLockModal={handleConfirmLockModal}
        handleCloseNoItemsLockModal={() => { setStatusForSomeItemsLockModal(null) }}
        statusForSubmittedToInProgressModal={statusForSubmittedToInProgressModal}
        handleConfirmSubmittedToInProgressModal={handleConfirmLockModal}
        handleCloseSubmittedToInProgressModal={() => { setStatusForSubmittedToInProgressModal(null) }}
        statusForSubmittedToAcceptedModal={statusForSubmittedToAcceptedModal}
        handleConfirmSubmittedToAcceptedModal={handleConfirmLockModal}
        handleCloseSubmittedToAcceptedModal={() => { setStatusForSubmittedToAcceptedModal(null) }}
        statusForSubmittedToNeedsRevisionModal={statusForSubmittedToNeedsRevisionModal}
        handleConfirmSubmittedToNeedsRevision={handleConfirmSubmittedToNeedsRevision}
        handleCloseSubmittedToNeedsRevision={() => {
          setStatusForSubmittedToNeedsRevisionModal(null)
        }}
        activities={props.priority.activities}
        planOrganizationTaskId={props.planOrganizationTask.id}
        priorityNumber={props.priority.priorityNumber}
      />
    </Card>
  </>
}
